import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateDocumentWorkflowTemplateProperties } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import {
	DocumentWorkflowTemplateVisibleIn,
	SignaturePlacementDto,
	DatePlacementDto,
	SignatureType,
} from '@dealstack/shared/lib/generated';
import {
	useTranslationDefault,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import {
	getBackendErrorMessage,
	isStandardBackendError,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { useParams } from 'react-router-dom';
import { useMasterDocumentsEndpoints } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useMasterDocumentsEndpoints.ts';
import { buildSignatureSlotsQueryKey } from './useSignatureSlotsQuery.ts';

interface Params {
	templateId: string;
	visibleIn?: DocumentWorkflowTemplateVisibleIn[];
	classifiers?: string[];
	signatureType?: SignatureType;
	instruction?: string;
	individualScheduleRequired?: boolean;
	individualInstances?: boolean;
	witnessRequired?: boolean;
	name?: string;
	counterparties?: Array<{
		name: string;
		numberOfSignaturesRequired: number;
		requiresWitness?: boolean;
	}>;
	useManualSignaturePlacement?: boolean;
	showStampMetadata?: boolean;
	signaturePlacements?: SignaturePlacementDto[];
	requiresPostDating?: boolean;
	datePlacements?: DatePlacementDto[];
}

export const useUpdateMasterDocument = () => {
	const tMipManage = useTranslationMipManage();
	const tDefault = useTranslationDefault();
	const queryClient = useQueryClient();
	const { list: listKey, byId } = useMasterDocumentsEndpoints();
	// Signature slots are deal-scoped, so only a programme edit has a key to invalidate.
	const { dealId, mipId } = useParams();

	return useMutation({
		mutationFn: async (params: Params): Promise<void> => {
			if (!params.templateId) {
				throw new Error('Template ID not found');
			}
			await updateDocumentWorkflowTemplateProperties({
				endpoint: byId(params.templateId),
				visibleIn: params.visibleIn,
				classifiers: params.classifiers,
				signatureType: params.signatureType,
				instruction: params.instruction,
				individualScheduleRequired: params.individualScheduleRequired,
				individualInstances: params.individualInstances,
				witnessRequired: params.witnessRequired,
				name: params.name,
				counterparties: params.counterparties,
				useManualSignaturePlacement: params.useManualSignaturePlacement,
				showStampMetadata: params.showStampMetadata,
				signaturePlacements: params.signaturePlacements,
				requiresPostDating: params.requiresPostDating,
				datePlacements: params.datePlacements,
			});
		},
		onSuccess: async (_, { templateId }) => {
			await queryClient.invalidateQueries({ queryKey: [listKey] });
			if (templateId) {
				await queryClient.invalidateQueries({ queryKey: [byId(templateId)] });
				if (dealId && mipId) {
					await queryClient.invalidateQueries({
						queryKey: [
							buildSignatureSlotsQueryKey({ dealId, programmeId: mipId, templateId }),
						],
					});
				}
			}
			showToastV2({
				title: tMipManage('master_documents.toast.updated'),
			});
		},
		onError: (error: unknown) => {
			if (isStandardBackendError(error)) {
				showToastV2({
					state: 'error',
					title: tDefault(`error_codes.${error.error?.code}`),
				});
				return;
			}
			// Where a settings-lock refusal lands: the global filter copies `code` onto the wire
			// only for a BusinessException, so a thrown `BadRequestException({ code, ... })`
			// reaches the client as its message alone. That message names the setting and says
			// why, which is more use than any single `error_codes.*` string could be.
			const backendMessage = getBackendErrorMessage(error);
			if (typeof backendMessage === 'string' && backendMessage.length > 0) {
				showToastV2({
					state: 'error',
					title: backendMessage,
				});
				return;
			}
			showToastV2({
				state: 'error',
				title: tDefault('common.toast.error.message'),
			});
		},
	});
};
